'use client';

import React, { useId, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * TeamWaitlistForm
 *
 * Shared inline Team-waitlist capture. Replaces the old mailto: links on the
 * pricing page (source: 'pricing') and the Billing tab (source: 'billing').
 *
 * Behavior: renders a trigger (CTA button or link, per `variant`); clicking
 * reveals a compact email + optional team-size form in place. On success the
 * form is replaced by a confirmation line. org_id is attached server-side
 * from the session — the client only sends email, teamSize, and source.
 */

type Phase = 'idle' | 'open' | 'pending' | 'success' | 'error';

export interface TeamWaitlistFormProps {
  source: 'pricing' | 'billing';
  /** Trigger label, e.g. "Reserve your team's ledger". */
  cta: string;
  /** 'button' — filled action CTA (pricing card). 'link' — text link (billing). */
  variant?: 'button' | 'link';
  className?: string;
}

export function TeamWaitlistForm({
  source,
  cta,
  variant = 'button',
  className,
}: TeamWaitlistFormProps): React.ReactElement {
  const [phase, setPhase] = useState<Phase>('idle');
  const [email, setEmail] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const emailId = useId();
  const sizeId = useId();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (phase === 'pending') return;
    setPhase('pending');

    const parsedSize = Number.parseInt(teamSize, 10);

    try {
      const res = await fetch('/api/v1/waitlist/team', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          teamSize: Number.isNaN(parsedSize) ? null : parsedSize,
          source,
        }),
      });
      setPhase(res.ok ? 'success' : 'error');
    } catch {
      setPhase('error');
    }
  }

  if (phase === 'success') {
    return (
      <p className={cn('font-sans text-sm text-ink', className)} role="status">
        You&rsquo;re on the list. We onboard teams in order.
      </p>
    );
  }

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={() => setPhase('open')}
        className={cn(
          variant === 'button'
            ? 'block w-full rounded-sm bg-action px-4 py-2.5 text-center font-sans text-sm font-medium text-action-foreground transition-colors hover:bg-action/90'
            : 'font-sans text-sm font-medium text-graphite underline-offset-4 transition-colors hover:text-ink hover:underline',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className,
        )}
      >
        {cta}
      </button>
    );
  }

  const inputClass =
    'w-full rounded-sm border border-rule bg-paper px-3 py-2 font-sans text-sm text-ink placeholder:text-graphite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <form onSubmit={handleSubmit} className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={emailId}
          className="font-sans text-xs font-medium uppercase tracking-[0.1em] text-graphite"
        >
          Work email
        </label>
        <input
          id={emailId}
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className={inputClass}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={sizeId}
          className="font-sans text-xs font-medium uppercase tracking-[0.1em] text-graphite"
        >
          Team size (optional)
        </label>
        <input
          id={sizeId}
          type="number"
          min={1}
          max={500}
          value={teamSize}
          onChange={(e) => setTeamSize(e.target.value)}
          placeholder="8"
          className={inputClass}
        />
      </div>
      {phase === 'error' && (
        <p className="font-sans text-sm text-burn" role="alert">
          That didn&rsquo;t go through. Check the email and try again.
        </p>
      )}
      <button
        type="submit"
        disabled={phase === 'pending'}
        className="rounded-sm bg-action px-4 py-2.5 font-sans text-sm font-medium text-action-foreground transition-colors hover:bg-action/90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {phase === 'pending' ? 'Joining…' : 'Join the waitlist'}
      </button>
    </form>
  );
}

TeamWaitlistForm.displayName = 'TeamWaitlistForm';
